import { defineComponent, FieldsMap, FieldViewModel, getConcurrentCrops, handleApiError, IOmniaShell, LocaleString, map, sum } from "../omnia"
import { ICroppingMergeModel, IFieldsEditService, IFieldsService } from "../types"
import { MergeCmd } from "./mergeCmd"
import { MergedCropViewModel } from "./mergedCropVm"
import { validateMerge } from "./validation"

/// <reference path="mergedCropVm.ts" />

import m = omnia.models
import { ol_Feature } from "@omnia/exports/openlayers"

export function createMergeComponent(): ng.IDirective {

    return defineComponent(MergeCtrl, "app/fields/merge/merge.html", {
        fieldsMap: "="
    })

}

export class MergeCtrl extends omnia.shell.Component {

    public fieldsMap: FieldsMap

    private _layer: MergeLayer

    public cropping: FieldViewModel

    public crops: MergedCropViewModel[] = []
    private target: MergedCropViewModel

    private _hasBoundaries: boolean
    private _mapSelectionWatcher: (features: any[]) => void
    private _fieldFeature: any
    private _fieldMapArea: number

    form: ng.IFormController
    showValidationErrors: boolean

    private _canMergeToFieldBoundary: boolean
    mergeToFieldBoundary: boolean

    invalidShape: boolean
    invalidNoBoundary: boolean

    static $inject = ["$scope", "$timeout", "OmniaShell", "OmniaFieldsService", "OmniaFieldsEditService", "OmniaMapTopologyService"]

    constructor($scope: ng.IScope, private $timeout: ng.ITimeoutService, private shell: IOmniaShell, private svc: IFieldsService, private editSvc: IFieldsEditService, private topo: omnia.map.IMapTopologyService) {

        super($scope, ["vm.fieldsMap"])

        this._icon = "icon-merge"
        this._title = "Merge crops" as LocaleString
    }

    get field(): FieldViewModel {
        return this.cropping.fieldVm
    }

    get retainedCrop(): MergedCropViewModel {
        return this.target
    }

    set retainedCrop(value: MergedCropViewModel) {
        this.target = value
    }

    getSelectedCropsCount(): number {
        return this.crops.filter(c => c.selected).length
    }

    protected setup() {

        var mergeCmd = <MergeCmd>this.execArguments

        if (!mergeCmd) {
            this.cropping = null
            return
        }

        this.setupCropping(mergeCmd.cropping)

        this.setupMap()

        this.svc.setView(this._hasBoundaries)

        this.editSvc.startEditing(true, this.cropping.cropping)

        this.selectInitialCropping(mergeCmd.cropping)

    }

    protected dispose() {

        this.cleanupMap()

    }

    cancel() {

        this.editSvc.stopEditing()

        this.cleanupMap()

        super.cancel()

    }

    private selectInitialCropping(cropping: FieldViewModel) {

        this.$timeout(() => {
            this.crops.forEach(c => c.selected = (c.crop == cropping))
        })

    }

    private setupCropping(cropping: FieldViewModel) {

        this.cropping = cropping

        const targets = getTargets(cropping)

        const crops = targets.map(s => this.svc.findCropping(s.CroppingId)).filter(s => !!s).sort((c1, c2) => c2.cropping.Area - c1.cropping.Area)

        const concurrent = (getConcurrentCrops(cropping.cropping, { filterRelated: false }) || []).concat(cropping.cropping)
        this._canMergeToFieldBoundary = (crops.length == concurrent.length) && (crops.every(c => concurrent.indexOf(c.cropping) >= 0))

        this.crops = crops.map(c => new MergedCropViewModel(this, c))

    }

    private setupMap() {

        this._hasBoundaries = this.crops.some(c => c.hasBoundary())
        if (!this._hasBoundaries) return

        const map = this.fieldsMap

        this._layer = new MergeLayer()
        map.addLayer(this._layer)

        const getArea = f => this.topo.measure.getArea(f)

        const features = this.crops.filter(c => c.hasBoundary()).map(c => this._layer.addFeature(c, getArea))

        map.setSelectableLayers([this._layer])

        map.synchronizeSelectionFromMap = false
        map.enableMultipleSelection = true

        this.updateMapSelection()

        var fieldFeature = map.findFeatureWithData(this.cropping.fieldVm)

        if (fieldFeature) {

            this._fieldFeature = fieldFeature
            this._fieldMapArea = getArea(fieldFeature)

            map.viewExtent(fieldFeature.getGeometry())

        }

        this.mergeToFieldBoundary = this.shouldMergeToFieldBoundary()

        map.croppingLayer.highlight(this.crops.map(c => c.crop))
        this._layer.setHighlightFeatures(features)

        map.watchSelection(this._mapSelectionWatcher = (features: any[]) => this.onMapSelectionChanged(features))

    }

    private cleanupMap() {

        if (!this._hasBoundaries) return

        var map = this.fieldsMap

        map.removeLayer(this._layer)

        map.unwatchSelection(this._mapSelectionWatcher)
        map.clearSelection()

        map.croppingLayer.clearHighlight()

        map.synchronizeSelectionFromMap = true
        map.enableMultipleSelection = false

        map.resetDefaultLayers(true)

    }


    private onMapSelectionChanged(features: any[]) {

        var selectedCrops = features.map(f => f.get("data").croppingId)

        this.crops.forEach(c => {

            var s = (selectedCrops.indexOf(c.croppingId) >= 0)
            c.setSelected(s, false)

        })

    }

    updateMapSelection() {

        var selection = this.crops.filter(c => c.selected).map(c => c.crop)

        var features = this.fieldsMap.findFeaturesWithData(selection)

        this.fieldsMap.selectFeatures(features)

    }

    private getMergedBoundary(): omnia.map.IMapTopologyGeometry {

        if (this.canMergeToFieldBoundary() && this.mergeToFieldBoundary) {

            return this.topo.getGeometry(this._fieldFeature.getGeometry())

        }

        let boundaries = this.crops.filter(c => c.selected && c.hasBoundary()).map(c => c.getBoundary())

        if (boundaries.length) {

            let union = this.topo.union(boundaries)

            if (!union) {
                boundaries = boundaries.map(b => this.topo.buffer(b, 0))
                union = this.topo.union(boundaries)
            }

            if (union) {
                union = this.topo.cleanupInteriorRings(union, 1)
            }

            return union

        }

    }

    canMergeToFieldBoundary(): boolean {

        return this._canMergeToFieldBoundary && this._fieldMapArea && this.crops.every(c => c.selected)


    }

    private shouldMergeToFieldBoundary(): boolean {

        var cropArea = this.crops.map(c => c.area).reduce((a1, a2) => a1 + a2)

        var diff = Math.abs(cropArea - this._fieldMapArea) / this._fieldMapArea

        return (diff <= 0.01)

    }


    canCommit(): boolean {

        if (!this.retainedCrop) return false

        var selected = this.crops.filter(c => c.selected)
        if (selected.length < 2) return false

        var selectedBoundaries = selected.filter(c => c.crop.hasBoundary())

        if ((!this.mergeToFieldBoundary) && selectedBoundaries.length && (selectedBoundaries.length < selected.length)) {
            this.invalidNoBoundary = true
            return false
        }

        return this.form && this.form.$valid
    }

    commit() {

        this.invalidNoBoundary = false
        this.invalidShape = false

        if (!this.canCommit()) {
            this.showValidationErrors = true
            return
        }

        this.showValidationErrors = false

        const boundary = this.getMergedBoundary()

        if (!boundary) {
            this.invalidShape = true
            return
        }

        if (!boundary.getOpenLayersPolygon()) {
            this.invalidShape = true
            return
        }

        this.invalidShape = false

        const selectedCrops = this.crops.filter(c => c.selected)

        validateMerge(selectedCrops, this.shell, this.svc).then(ok => {

            if (!ok) return

            const retained = this.retainedCrop

            const crops = selectedCrops.filter(c => c != retained)

            let area: number

            if (this.canMergeToFieldBoundary() && this.mergeToFieldBoundary) {
                area = this.field.area
            } else {
                area = sum(selectedCrops, c => c.cropping.Area)
            }

            const model: ICroppingMergeModel = {

                HoldingId: this.svc.holdingId,
                YearId: retained.cropping.YearId,
                FieldId: retained.cropping.FieldId,
                CroppingId: retained.croppingId,

                Area: area,
                BoundaryWKT: (boundary ? boundary.getWKT() : null),

                MergedCrops: crops.map(c => c.croppingId)

            }

            this.svc.mergeCropping(model)
                .then(() => this.cancel())
                .catch(e => handleApiError(this.shell, e))

        })

    }

    private selectAll() {
        this.crops.forEach(c => c.selected = true)
    }

    private unselectAll() {
        this.crops.forEach(c => c.selected = false)
    }

    private reverseSelection() {
        this.crops.forEach(c => c.selected = !c.selected)
    }

}

class MergeLayer extends omnia.map.Layer {

    addFeature(crop: MergedCropViewModel, getArea: (g: ol_Feature) => number) {

        const feature = super.createNewFeature(crop.getBoundary(), crop.crop)

        crop.area = getArea(feature)

        return feature

    }

}

function getTargets(cropping: FieldViewModel) {

    const src = cropping.cropping

    const concurrent = getConcurrentCrops(src, { filterRelated: false, year: src.YearId })

    const targets = concurrent.concat([src])

    return targets

}
